import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';
import {
  Ticket, AlertCircle, CheckCircle, Clock, TrendingUp,
  BarChart3, Shield, FileDown, Loader2, List, Search, X
} from 'lucide-react';
import usePdfExport from '../hooks/usePdfExport';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const TicketReport = ({ tickets, stats, openCount }) => {
  const [listSearch, setListSearch] = useState('');
  const [viewMode, setViewMode] = useState('chart');

  const filteredTickets = useMemo(() => {
    if (!listSearch) return tickets;
    const term = listSearch.toLowerCase();
    return tickets.filter(t =>
      t.title?.toLowerCase().includes(term) ||
      t.ticketNumber?.toLowerCase().includes(term) ||
      t.bagian?.toLowerCase().includes(term) ||
      t.reportedBy?.name?.toLowerCase().includes(term)
    );
  }, [tickets, listSearch]);

  const { exporting, exportPdf, contentRef } = usePdfExport({
    filename: 'ticket-report',
    scale: 3,
  });

  const s = stats?.total || { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0, urgent: 0 };
  const allTickets = tickets || [];

  // ─── Chart Data ──────────────────────────────────────────
  const statusData = useMemo(() => {
    if (stats?.statuses?.length > 0) return stats.statuses.map(st => ({ name: st.name, value: st.count }));
    const count = {};
    allTickets.forEach(t => { count[t.status] = (count[t.status] || 0) + 1; });
    return Object.entries(count).map(([name, value]) => ({ name, value }));
  }, [allTickets, stats]);

  const priorityData = useMemo(() => {
    if (stats?.priorities?.length > 0) return stats.priorities.map(p => ({ name: p.name, value: p.count }));
    const count = {};
    allTickets.forEach(t => { count[t.priority] = (count[t.priority] || 0) + 1; });
    return Object.entries(count).map(([name, value]) => ({ name, value }));
  }, [allTickets, stats]);

  const bagianData = useMemo(() => {
    const count = {};
    allTickets.forEach(t => { count[t.bagian] = (count[t.bagian] || 0) + 1; });
    return Object.entries(count).map(([name, value]) => ({ name: name || 'Umum', value })).sort((a, b) => b.value - a.value);
  }, [allTickets]);

  const monthlyData = useMemo(() => {
    if (stats?.monthly?.length > 0) return stats.monthly.map(m => ({ month: m.month, tickets: m.count }));
    const count = {};
    allTickets.forEach(t => {
      const month = format(new Date(t.createdAt), 'MMM yyyy');
      count[month] = (count[month] || 0) + 1;
    });
    return Object.entries(count)
      .map(([month, tickets]) => ({ month, tickets }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [allTickets, stats]);

  const resolutionRate = s.total > 0
    ? Math.round(((s.resolved + s.closed) / s.total) * 100)
    : 0;

  // ─── Colors ──────────────────────────────────────────────
  const STATUS_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#6B7280'];
  const PRIORITY_COLORS = ['#3B82F6', '#F59E0B', '#EF4444'];
  const BAGIAN_COLORS = ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  const summaryCards = [
    {
      title: 'Total Tickets',
      value: s.total,
      icon: Ticket,
      bgLight: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      subtitle: 'All time tickets',
    },
    {
      title: 'Open',
      value: s.open,
      icon: AlertCircle,
      bgLight: 'bg-red-50',
      iconColor: 'text-red-600',
      subtitle: 'Needs attention',
      badge: openCount > 0 ? `${openCount} open` : null,
    },
    {
      title: 'In Progress',
      value: s.inProgress,
      icon: Clock,
      bgLight: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      subtitle: 'Being handled',
    },
    {
      title: 'Resolved',
      value: s.resolved,
      icon: CheckCircle,
      bgLight: 'bg-green-50',
      iconColor: 'text-green-600',
      subtitle: 'Completed',
      progress: resolutionRate,
    },
    {
      title: 'Closed',
      value: s.closed || 0,
      icon: Shield,
      bgLight: 'bg-gray-50',
      iconColor: 'text-gray-600',
      subtitle: 'Fully closed',
    },
    {
      title: 'Urgent',
      value: s.urgent || 0,
      icon: AlertCircle,
      bgLight: 'bg-rose-50',
      iconColor: 'text-rose-600',
      subtitle: 'High priority',
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" ref={contentRef}>
      {/* ─── Header ───────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ticket Report</h2>
          <p className="mt-1 text-sm text-gray-500">
            Analytics and insights for all support tickets
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'chart' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <BarChart3 className="w-3.5 h-3.5 inline mr-1" />Chart
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <List className="w-3.5 h-3.5 inline mr-1" />List
            </button>
          </div>
          <div className="flex items-center space-x-2 bg-indigo-50 rounded-lg px-3 py-2">
            <BarChart3 className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">{resolutionRate}% resolved</span>
          </div>
          <button
            onClick={() => exportPdf('Ticket-Report')}
            disabled={exporting}
            className="btn-primary text-sm"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            <span>{exporting ? 'Exporting...' : 'Export PDF'}</span>
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        /* ─── List View ──────────────────────────────────────── */
        <div className="space-y-4">
          <div className="card">
            <div className="px-4 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tickets by title, number, bagian, or reporter..."
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
                />
                {listSearch && (
                  <button onClick={() => setListSearch('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reported By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {filteredTickets.length > 0 ? (
                    filteredTickets.map((t, idx) => (
                      <tr key={t._id} className="hover:bg-gray-50 transition-colors" style={{ animationDelay: `${idx * 20}ms` }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{t.ticketNumber}</span>
                            <span className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">{t.title}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{t.bagian}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
                            t.status === 'Open' ? 'bg-red-100 text-red-700 border-red-200' :
                            t.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                            t.status === 'Resolved' ? 'bg-green-100 text-green-700 border-green-200' :
                            'bg-gray-100 text-gray-600 border-gray-200'
                          }`}>{t.status}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
                            t.priority === 'Urgent' ? 'bg-red-100 text-red-700 border-red-200' :
                            t.priority === 'High' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                            t.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                            'bg-blue-100 text-blue-700 border-blue-200'
                          }`}>{t.priority}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {t.reportedBy?.name || t.reportedBy?.username || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {format(new Date(t.createdAt), 'MMM dd, yyyy HH:mm')}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                            <AlertCircle className="w-7 h-7 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium">No tickets found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {listSearch ? 'Try adjusting your search' : 'No tickets have been submitted yet'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{filteredTickets.length}</span> of{' '}
                <span className="font-medium text-gray-700">{tickets.length}</span> tickets
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ─── Urgent Alert ─────────────────────────────────── */}
          {s.urgent > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 animate-slide-up">
              <div className="flex items-center space-x-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                </span>
                <p className="text-sm font-semibold text-red-800">
                  {s.urgent} urgent ticket{s.urgent > 1 ? 's' : ''} need{s.urgent === 1 ? 's' : ''} immediate attention!
                </p>
              </div>
            </div>
          )}

          {/* ─── Summary Cards ────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {summaryCards.map((card, index) => (
              <div
                key={card.title}
                className="card animate-slide-up overflow-hidden"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className={`p-2.5 rounded-xl ${card.bgLight}`}>
                      <card.icon className={`w-5 h-5 ${card.iconColor}`} />
                    </div>
                    {card.badge && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-500">{card.title}</p>
                    <p className="mt-0.5 text-2xl font-bold text-gray-900">{card.value}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{card.subtitle}</p>
                  </div>
                  {card.progress !== undefined && s.total > 0 && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-green-500 to-emerald-400 h-1.5 rounded-full transition-all duration-1000"
                          style={{ width: `${card.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ─── Charts Grid ───────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <h3 className="section-title">Tickets by Status</h3>
                </div>
              </div>
              <div className="card-body">
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statusData} barSize={60}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {statusData.map((_, i) => (
                          <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-gray-400">No data available</div>
                )}
              </div>
            </div>

            {/* Priority Distribution */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <h3 className="section-title">Tickets by Priority</h3>
                </div>
              </div>
              <div className="card-body">
                {priorityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={priorityData}
                        cx="50%" cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={90} innerRadius={30}
                        dataKey="value"
                      >
                        {priorityData.map((_, i) => (
                          <Cell key={i} fill={PRIORITY_COLORS[i % PRIORITY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-gray-400">No data available</div>
                )}
              </div>
            </div>

            {/* Bagian Distribution */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <h3 className="section-title">Tickets by Bagian</h3>
                </div>
              </div>
              <div className="card-body">
                {bagianData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={bagianData} barSize={40} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 13 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                        {bagianData.map((_, i) => (
                          <Cell key={i} fill={BAGIAN_COLORS[i % BAGIAN_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-gray-400">No data available</div>
                )}
              </div>
            </div>

            {/* Monthly Trend */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <h3 className="section-title">Monthly Ticket Trend</h3>
                </div>
              </div>
              <div className="card-body">
                {monthlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone" dataKey="tickets"
                        stroke="#6366F1" strokeWidth={3}
                        dot={{ fill: '#6366F1', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 7, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-gray-400">No monthly data available</div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Empty State ──────────────────────────────────── */}
          {allTickets.length === 0 && (
            <div className="card p-12 text-center animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <TrendingUp className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg font-medium">No tickets to report</p>
              <p className="text-gray-400 mt-1">Submit some tickets to see analytics and insights</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TicketReport;
